import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useConversationUuid } from '~/hooks/conversation/useConversationUuid'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { AgentTaskStatus, MigrationSelectedEndpoint } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { CheckCircleIcon, EmptyState, LoadingContainer, LoadingSkeletonItem } from '../common'

interface Endpoint {
  id: string
  name: string
  category?: string
  filePath?: string
}

interface EndpointSelectionSlideProps {
  taskStatus?: AgentTaskStatus
  onSelectionChange?: (selectedCount: number, selectedEndpoints: Endpoint[]) => void
}

/**
 * Converts MigrationSelectedEndpoint from proto to local Endpoint interface
 */
function convertToEndpoints(endpoints: MigrationSelectedEndpoint[]): Endpoint[] {
  return endpoints.map((ep, index) => ({
    id: String(index),
    name: ep.name || `Endpoint ${index + 1}`,
    category: ep.category || undefined,
    filePath: ep.filePath || ''
  }))
}

/**
 * Parses legacy jsonMetadata (double-encoded JSON) to extract endpoints.
 * Used as fallback for tasks created before the identifiedEndpoints field was added.
 * Format: { refactor_obj: "{...}" } -> refactor_obj.migration.git_repos_endpoints
 */
function parseEndpointsFromLegacyMetadata(jsonMetadata: string): Endpoint[] {
  try {
    const metadata = JSON.parse(jsonMetadata)
    const refactorObjStr = metadata.refactor_obj

    if (!refactorObjStr) {
      return []
    }

    const refactorObj =
      typeof refactorObjStr === 'string' ? JSON.parse(refactorObjStr) : refactorObjStr
    const legacyEndpoints = refactorObj?.migration?.git_repos_endpoints || []

    return legacyEndpoints.map(
      (
        ep: {
          id?: string
          name?: string
          endpoint?: string
          category?: string
          file_loc?: string
          filePath?: string
        },
        index: number
      ) => ({
        id: ep.id || String(index),
        name: ep.name || ep.endpoint || `Endpoint ${index + 1}`,
        category: ep.category,
        filePath: ep.file_loc || ep.filePath || ''
      })
    )
  } catch (e) {
    console.error('Failed to parse legacy jsonMetadata for endpoints:', e)
    return []
  }
}

/**
 * Loading view shown while the backend identifies endpoints. Rendered by
 * EndpointSelectionSlide once a real task is in flight (i.e. after the task_id
 * exists and the conversation page has mounted). The repo-selection screen shows
 * a plain spinner during task creation instead, so this "Identifying Endpoints…"
 * view only appears for an actual task.
 */
export function EndpointSelectionLoading() {
  const isPlugin = bridge.isInPlugin()
  return (
    <div className='w-full h-full overflow-hidden flex flex-col'>
      <div
        className={`flex justify-center w-full flex-1 min-h-0 overflow-y-auto ${isPlugin ? 'px-3' : 'px-[60px]'}`}
      >
        <div
          className={`w-full flex flex-col ${isPlugin ? 'max-w-full pb-6' : 'max-w-[720px] pb-[60px]'}`}
        >
          {/* Modal Header */}
          <div className='flex-shrink-0 flex flex-col gap-[16px] items-center pt-[60px]'>
            <p className="font-['Geist'] font-bold text-[20px] leading-[22px] text-black text-center">
              Identifying Endpoints
            </p>
            <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] text-center">
              Analyzing your repository to identify API endpoints...
            </p>
          </div>

          {/* Skeleton */}
          <div className='flex flex-col gap-[24px] items-center w-full mt-[36px]'>
            <div className='flex flex-col gap-[16px] items-start w-full'>
              <LoadingContainer>
                {/* Empty category header space */}
                <div className='flex flex-col gap-[4px] items-start justify-center px-[24px] py-[8px] w-full'>
                  <div className='flex flex-col gap-[4px] items-start w-full' />
                </div>

                {/* Skeleton loading items */}
                <LoadingSkeletonItem count={3} />
              </LoadingContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function EndpointSelectionSlide({
  taskStatus,
  onSelectionChange
}: EndpointSelectionSlideProps) {
  const isPlugin = bridge.isInPlugin()
  const [searchParams] = useSearchParams()
  const conversationUuid = useConversationUuid()
  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [selectedEndpoints, setSelectedEndpoints] = useState<Set<string>>(new Set())

  const taskId = searchParams.get('task_id') ?? ''
  const { data: taskDetails } = useTaskDetails(conversationUuid, taskId)

  // Loading state is determined solely by task status
  const isLoading = taskStatus !== AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED

  // Extract endpoints from task data using identifiedEndpoints field (new format)
  // with fallback to jsonMetadata for legacy tasks
  useEffect(() => {
    if (!taskDetails?.task?.details?.orderedStateResponses) {
      return
    }

    const tuples = taskDetails.task.details.orderedStateResponses

    // Find the tuple with MIGRATION_ENDPOINTS_IDENTIFIED status
    for (const tuple of tuples) {
      if (tuple.taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED) {
        const identifiedEndpoints = tuple.response?.[0]?.responseContentStream?.identifiedEndpoints
        const jsonMetadata = tuple.response?.[0]?.responseContentStream?.jsonMetadata

        if (identifiedEndpoints && identifiedEndpoints.length > 0) {
          // New format: use identifiedEndpoints directly
          setEndpoints(convertToEndpoints(identifiedEndpoints))
          break
        } else if (jsonMetadata) {
          // Legacy fallback: parse from jsonMetadata
          const legacyEndpoints = parseEndpointsFromLegacyMetadata(jsonMetadata)
          if (legacyEndpoints.length > 0) {
            setEndpoints(legacyEndpoints)
            break
          }
        }
      }
    }
  }, [taskDetails])

  // Select all endpoints by default when they are first loaded
  useEffect(() => {
    if (endpoints.length > 0 && selectedEndpoints.size === 0) {
      setSelectedEndpoints(new Set(endpoints.map(ep => ep.id)))
    }
  }, [endpoints])

  const toggleEndpoint = (id: string) => {
    setSelectedEndpoints(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // Notify parent when selection changes
  useEffect(() => {
    const selected = endpoints.filter(ep => selectedEndpoints.has(ep.id))
    onSelectionChange?.(selectedEndpoints.size, selected)
  }, [selectedEndpoints, endpoints, onSelectionChange])

  // Group endpoints by category
  const groupedEndpoints = endpoints.reduce(
    (acc, endpoint) => {
      const category = endpoint.category || 'OTHER'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(endpoint)
      return acc
    },
    {} as Record<string, Endpoint[]>
  )

  // Endpoints are still being identified — show the shared loading skeleton.
  if (isLoading) {
    return <EndpointSelectionLoading />
  }

  return (
    <div className='w-full h-full overflow-hidden flex flex-col'>
      <div
        className={`flex justify-center w-full flex-1 min-h-0 overflow-y-auto ${isPlugin ? 'px-3' : 'px-[60px]'}`}
      >
        <div
          className={`w-full flex flex-col ${isPlugin ? 'max-w-full pb-6' : 'max-w-[720px] pb-[60px]'}`}
        >
          {/* Modal Header */}
          <div className='flex-shrink-0 flex flex-col gap-[16px] items-center pt-[60px]'>
            <p className="font-['Geist'] font-bold text-[20px] leading-[22px] text-black text-center">
              Select Endpoints
            </p>
            <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] text-center">
              {
                "Here's the identified endpoints based your repositories. Select the ones you want the agent to refactor."
              }
            </p>
          </div>

          {/* Content */}
          {endpoints.length === 0 ? (
            <EmptyState
              message='No endpoints found'
              description="We couldn't identify any endpoints in your repository. Please check your repository configuration or try again."
              className='flex-1 mt-[36px]'
            />
          ) : (
            <div className='flex flex-col gap-[24px] items-start w-full mt-[36px]'>
              <div className='flex flex-col gap-[16px] items-start w-full'>
                <div className='flex flex-col gap-[24px] items-start w-full'>
                  {/* Container with rounded background */}
                  <div className='bg-[rgba(189,189,189,0.08)] flex flex-col gap-[24px] items-start px-[24px] py-[16px] rounded-[28px] w-full'>
                    {Object.entries(groupedEndpoints).map(([category, categoryEndpoints]) => (
                      <div key={category} className='flex flex-col gap-[8px] items-start w-full'>
                        {/* Category Header */}
                        <div className='flex flex-col gap-[4px] items-start justify-center px-[24px] py-[16px] w-full'>
                          <div className='flex gap-[4px] items-center w-full'>
                            <p className="font-['Geist'] font-bold text-[14px] leading-[18px] text-[rgba(0,0,0,0.88)] whitespace-nowrap">
                              {category}
                            </p>
                            <div className='bg-[rgba(0,0,0,0.06)] flex gap-[4px] h-[16px] items-center px-[8px] py-[1px] rounded-[50px]'>
                              <p className="font-['Geist'] font-bold text-[10px] leading-normal text-[rgba(0,0,0,0.45)]">
                                {categoryEndpoints.length}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Endpoint List Items */}
                        {categoryEndpoints.map(endpoint => (
                          <button
                            key={endpoint.id}
                            className='border-[#eaeaea] border-b border-solid flex flex-col h-[48px] items-start justify-center px-0 py-[4px] w-full cursor-pointer bg-transparent'
                            onClick={() => toggleEndpoint(endpoint.id)}
                          >
                            <div className='flex gap-[8px] items-center pl-[12px] pr-0 py-0 w-full'>
                              {/* Check Circle Icon */}
                              <div className='flex items-center justify-center w-[36px] h-[36px]'>
                                <CheckCircleIcon
                                  checked={selectedEndpoints.has(endpoint.id)}
                                  size={16}
                                />
                              </div>
                              <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                                {endpoint.name}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
